const morphdom =
    require('morphdom').default; /** Lightweight module for morphing an existing DOM node tree to match a target DOM node tree {@link https://github.com/patrick-steele-idem/morphdom#readme} */
const Sqrl = require('squirrelly'); /** SquirrellyJS - Powerful, lightweight, pluggable JS template engine {@link https://squirrelly.js.org/} */

/**
 * @typedef {Object} MorphDomOptions
 * @property {function(Node): any} [getNodeKey] - Function to get the unique key of a node.
 * @property {function(Node): Node} [onBeforeNodeAdded] - Function called before a node is added to the tree.
 * @property {function(Node): void} [onNodeAdded] - Function called after a node is added to the tree.
 * @property {function(HTMLElement, HTMLElement): boolean} [onBeforeElUpdated] - Function called before an element is updated.
 * @property {function(HTMLElement): void} [onElUpdated] - Function called after an element is updated.
 * @property {function(Node): boolean} [onBeforeNodeDiscarded] - Function called before a node is discarded.
 * @property {function(Node): void} [onNodeDiscarded] - Function called after a node is discarded.
 * @property {function(HTMLElement, HTMLElement): boolean} [onBeforeElChildrenUpdated] - Function called before the children of an element are updated.
 * @property {Object} [data] - Custom data to be passed into Squirrelly template.
 * @property {boolean} [childrenOnly] - Whether to only update the children of the element.
 */

/**
 * @typedef {Object} PluginSettings
 * @property {string} id - template id or htmlString
 * @property {Object} squirrellyOptions - template options
 * @property {MorphDomOptions} morphDomOptions - morphdom options
 */

/**
 * @module MorphBy
 * @exports MorphBy
 * @param {PluginSettings | string} custopOptions - morphdom options
 * @returns {jQuery} - Append template to the container
 */
function MorphBy(custopOptions = {}) {
    let options = custopOptions;

    if (typeof options === 'string') {
        options = { id: custopOptions };
    }

    const { id, squirrellyOptions, morphDomOptions } = { ...MorphBy.defaultOptions, ...options };

    const html = MorphBy.getTemplate(id, squirrellyOptions);

    return this.each((i, el) => {
        if (morphDomOptions.morph) {
            const morphHtmlTarget = morphDomOptions.childrenOnly ? `<div>${html}</div>` : html;

            const newDom = morphdom(el, morphHtmlTarget, {
                ...MorphBy.defaultOptions.morphDomOptions,
                ...morphDomOptions
            });

            if (!morphDomOptions.childrenOnly) {
                this[i] = newDom;
            }
        } else if (!morphDomOptions.childrenOnly) {
            const $html = $(html);
            $(el).replaceWith($html);
            this[i] = $html;
        } else {
            $(el).html($(html));
        }

    });
}

MorphBy.defaultOptions = {
    squirrellyOptions: {},
    morphDomOptions: {
        childrenOnly: true,
        morph: false,
        onBeforeElUpdated(fromEl, toEl) {
            if (
                fromEl.tagName === 'IMG' &&
                fromEl.tagName === toEl.fromEl &&
                fromEl.getAttribute('src') !== toEl.getAttribute('src')
            ) {
                fromEl.onload = () => {
                    fromEl.style.visibility = '';
                };

                fromEl.style.visibility = 'hidden';
                fromEl.src = toEl.getAttribute('src');

                return false;
            }

            return !fromEl.hasAttribute('data-morph-ignore') || !toEl.hasAttribute('data-morph-ignore');
        }
    }
};

MorphBy.initialized = false;

MorphBy.getTemplate = (id, squirrellyOptions = {}) => {
    const { templateId } = squirrellyOptions;
    const idSelector = MorphBy.parseId(id);

    if (!MorphBy.initialized) {
        MorphBy.defineAllTemplates();
    }
    const definedTemplate = MorphBy.defineTemplate(templateId ? MorphBy.parseId(templateId) : idSelector);

    MorphBy.initFilters();

    return Sqrl.render(definedTemplate, { ...MorphBy.defaultOptions.squirrellyOptions, ...squirrellyOptions });
};

MorphBy.defineAllTemplates = () => {
    MorphBy.initialized = true;

    const $sqrlTemplates = $("script[type='text/x-squirrelly-template']");
    $sqrlTemplates.each((i, el) => {
        MorphBy.defineTemplate(`#${el.id}`);
    });
};

MorphBy.defineTemplate = (idSelector) => {
    if (Sqrl.templates.get(idSelector)) {
        return Sqrl.templates.get(idSelector);
    }

    const html = $(idSelector).text() || "Template doesn't exist.";
    const trimmedHtml = html.replace(/<!--[\s\S]*?-->/g, '').trim();

    const compiledTemplate = Sqrl.compile(trimmedHtml);
    Sqrl.templates.define(idSelector, compiledTemplate);

    return compiledTemplate;
};

MorphBy.parseId = (id) => {
    return id.startsWith('#') ? id : `#${id}`;
};

MorphBy.initFilters = () => {
    Sqrl.filters.define('boldMatch', (str, search) =>
        str.replace(search, (match) => `<strong class="highlighted-text">${match}</strong>`)
    );
    Sqrl.filters.define('join', (arr, separator) => arr.join(separator));
};

module.exports = MorphBy;
